import React, { useState, useEffect, useCallback } from "react";
import { API, getToken } from "../api";

// ───────────────────────────────────────────────────────────────────────────
// Reports → Bordereau (BDX).  Premium client-facing claim bordereau console.
// Single backend generator feeds preview + exceptions + export so numbers,
// readiness, and scope never drift.  Cap-gated (view_bordereau_reports) at the
// Reports selector — ordinary adjusters never reach this surface.
// ───────────────────────────────────────────────────────────────────────────

const NAVY = "#1F3864", BLUE = "#2E75B6", SLATE = "#64748b";
const SEM = {
  ready:   { bg: "#E6F4EA", fg: "#1E7E34", label: "Ready" },
  warning: { bg: "#FFF4CE", fg: "#8A6D00", label: "Warning" },
  error:   { bg: "#FBE4E4", fg: "#A12121", label: "Error" },
};

const money = (v) => (v == null ? "—" :
  "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }));
const pct = (v) => (v == null ? "—" : Number(v).toFixed(1) + "%");

async function apiGet(path) {
  const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
}

function firstOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() { return new Date().toISOString().slice(0, 10); }

function StatusChip({ status }) {
  const s = SEM[status] || SEM.warning;
  return <span style={{ background: s.bg, color: s.fg, fontWeight: 700, fontSize: 11,
    padding: "2px 9px", borderRadius: 12, whiteSpace: "nowrap" }}>{s.label}</span>;
}

function Card({ label, value, accent }) {
  return (
    <div style={{ background: "white", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8,
      padding: "12px 16px", minWidth: 120, flex: "0 0 auto" }}>
      <div style={{ fontSize: 11, color: SLATE, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent || NAVY, marginTop: 2 }}>{value}</div>
    </div>
  );
}

export default function BordereauReports({ onSelectClaim, initialViewState, onViewStateChange }) {
  const iv = initialViewState || {};   // REPORTS-UX-1: restore a saved/pinned view
  const [profiles, setProfiles] = useState([]);
  const [variants, setVariants] = useState([]);
  const [profileId, setProfileId] = useState(iv.profileId ?? null);
  const [variant, setVariant] = useState(iv.variant ?? "standard");
  const [start, setStart] = useState(iv.start ?? firstOfMonth());
  const [end, setEnd] = useState(iv.end ?? today());
  const [tab, setTab] = useState(iv.tab ?? "preview");
  const [payload, setPayload] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  // REPORTS-UX-1: emit serialized view state up for pin/recent/saved-view capture.
  useEffect(() => {
    onViewStateChange && onViewStateChange({ profileId, variant, start, end, tab });
    /* eslint-disable-next-line */
  }, [profileId, variant, start, end, tab]);

  useEffect(() => {
    apiGet("/reports/bordereau/profiles").then((d) => {
      setProfiles(d.profiles || []); setVariants(d.variants || []);
      if (d.profiles && d.profiles.length) setProfileId(d.profiles[0].company_id);
    }).catch((e) => setError(String(e.message || e)));
  }, []);

  const activeProfile = profiles.find((p) => p.company_id === profileId);
  // profileId now holds the selected CLIENT company_id; template_id (column
  // spec) comes from the matched profile entry.

  const runPreview = useCallback(async () => {
    if (!profileId) return;
    const ap = profiles.find((p) => p.company_id === profileId);
    const base = `template_id=${ap ? ap.template_id : ""}&client_company_id=${profileId}&start=${start}&end=${end}`;
    setLoading(true); setError(null);
    try {
      const [pv, ex] = await Promise.all([
        apiGet(`/reports/bordereau/preview?${base}&variant=${variant}`),
        apiGet(`/reports/bordereau/exceptions?${base}`),
      ]);
      setPayload(pv); setExceptions(ex);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, [profileId, profiles, start, end, variant]);

  const loadRuns = useCallback(async () => {
    try { setRuns(await apiGet("/reports/bordereau/runs?limit=50")); }
    catch (e) { setError(String(e.message || e)); }
  }, []);

  useEffect(() => { if (tab === "runs" && !runs) loadRuns(); }, [tab, runs, loadRuns]);

  async function doExport() {
    if (!profileId) return;
    setExporting(true); setError(null);
    try {
      const ap = profiles.find((p) => p.company_id === profileId);
      const qs = `template_id=${ap ? ap.template_id : ""}&client_company_id=${profileId}&start=${start}&end=${end}&variant=${variant}`;
      const r = await fetch(`${API}/reports/bordereau/export?${qs}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`Export failed (HTTP ${r.status})`);
      const disp = r.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "bordereau.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      if (runs) loadRuns();
    } catch (e) { setError(String(e.message || e)); }
    finally { setExporting(false); }
  }

  const sum = payload?.summary;
  const showShare = payload?.show_insurer_share;
  const showRebate = payload?.show_fee_rebate;

  const ctrl = { padding: "7px 10px", border: "1px solid var(--border, #cbd5e1)", borderRadius: 5, background: "white", fontSize: 14 };
  const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3, color: "white", background: NAVY, position: "sticky", top: 0, whiteSpace: "nowrap" };
  const thR = { ...th, textAlign: "right" };
  const td = { padding: "7px 10px", borderBottom: "1px solid var(--border-light, #f1f5f9)", fontSize: 13, whiteSpace: "nowrap" };
  const tdR = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

  return (
    <div style={{ padding: "4px 2px" }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, color: NAVY, fontSize: 22 }}>Bordereau Reports</h2>
        <div style={{ color: SLATE, fontSize: 13, marginTop: 2 }}>
          Generate client-ready claim bordereaux with allocation, validation, and export controls.
        </div>
      </div>

      {/* Control card */}
      <div style={{ background: "var(--bg-subtle, #f8fafc)", border: "1px solid var(--border, #e2e8f0)",
        borderRadius: 10, padding: 16, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: SLATE, fontWeight: 600 }}>
          CLIENT / PROFILE
          <select style={{ ...ctrl, minWidth: 280 }} value={profileId || ""} onChange={(e) => setProfileId(Number(e.target.value))}>
            {profiles.map((p) => <option key={p.company_id} value={p.company_id}>{p.company_name}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: SLATE, fontWeight: 600 }}>
          VARIANT
          <div style={{ display: "flex", border: "1px solid var(--border, #cbd5e1)", borderRadius: 5, overflow: "hidden" }}>
            {(variants.length ? variants : [{ key: "standard", label: "Standard LCRS" }, { key: "extended", label: "Walters Extended" }]).map((v) => (
              <button key={v.key} onClick={() => setVariant(v.key)} title={v.desc || ""}
                style={{ padding: "7px 12px", border: "none", cursor: "pointer", fontSize: 13,
                  background: variant === v.key ? BLUE : "white", color: variant === v.key ? "white" : "#334155", fontWeight: variant === v.key ? 700 : 500 }}>
                {v.label}
              </button>
            ))}
          </div>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: SLATE, fontWeight: 600 }}>
          PERIOD START
          <input type="date" style={ctrl} value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: SLATE, fontWeight: 600 }}>
          PERIOD END
          <input type="date" style={ctrl} value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <button onClick={runPreview} disabled={loading || !profileId}
          style={{ padding: "9px 18px", background: NAVY, color: "white", border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}>
          {loading ? "Running…" : "Run Preview"}
        </button>
        <button onClick={doExport} disabled={exporting || !payload}
          style={{ padding: "9px 18px", background: payload ? BLUE : "#cbd5e1", color: "white", border: "none", borderRadius: 6, fontWeight: 700, cursor: payload ? "pointer" : "default" }}>
          {exporting ? "Exporting…" : "Export XLSX"}
        </button>
      </div>

      {error && <div style={{ background: SEM.error.bg, color: SEM.error.fg, padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}

      {/* Summary strip */}
      {sum && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <Card label="In Scope" value={sum.in_scope} />
          <Card label="Ready" value={sum.ready} accent={SEM.ready.fg} />
          <Card label="Warnings" value={sum.warnings} accent={SEM.warning.fg} />
          <Card label="Errors" value={sum.errors} accent={SEM.error.fg} />
          <Card label="Total Incurred (100%)" value={money(sum.total_incurred)} />
          {showShare && <Card label="Insurer Share Incurred" value={money(sum.insurer_share_incurred)} accent={BLUE} />}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid var(--border, #e2e8f0)", marginBottom: 12 }}>
        {[["preview", "Preview"], ["exceptions", `Exceptions${exceptions ? ` (${exceptions.exceptions.length})` : ""}`], ["runs", "Run History"], ["notes", "Profile Notes"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 14,
              fontWeight: tab === k ? 700 : 500, color: tab === k ? NAVY : SLATE,
              borderBottom: tab === k ? `2px solid ${NAVY}` : "2px solid transparent", marginBottom: -2 }}>
            {lbl}
          </button>
        ))}
      </div>

      {/* PREVIEW */}
      {tab === "preview" && (
        !payload ? <div style={{ color: SLATE, padding: 30, textAlign: "center" }}>Run a preview to see bordereau rows.</div> :
        <div style={{ overflowX: "auto", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, maxHeight: "60vh" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Status</th><th style={th}>Claim #</th><th style={th}>Insured</th>
                <th style={thR}>Indemnity</th><th style={thR}>Fees</th><th style={thR}>Reserve</th><th style={thR}>Incurred (100%)</th>
                {showShare && <><th style={thR}>Share %</th><th style={thR}>Share Incurred</th></>}
                {showRebate && <><th style={thR}>Fee Reduction</th><th style={thR}>Net Fees Due</th></>}
                <th style={th}>Issues</th>
              </tr>
            </thead>
            <tbody>
              {payload.rows.map((r) => {
                const f = r.financials || {}, sh = r.share || {}, rb = r.rebate || {};
                const rowBg = r.status === "error" ? SEM.error.bg : r.status === "warning" ? "#fffdf5" : "white";
                return (
                  <tr key={r.claim_id} style={{ background: rowBg }}>
                    <td style={td}><StatusChip status={r.status} /></td>
                    <td style={{ ...td, color: BLUE, cursor: onSelectClaim ? "pointer" : "default", fontWeight: 600 }}
                      onClick={() => onSelectClaim && onSelectClaim(r.claim_id)}>{r.claim_number}</td>
                    <td style={td}>{r.insured_name || "—"}</td>
                    <td style={tdR}>{money(f.total_incurred_indemnity)}</td>
                    <td style={tdR}>{money(f.total_incurred_fees)}</td>
                    <td style={tdR}>{money(f.reserve_indemnity)}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{money(f.total_incurred_all)}</td>
                    {showShare && <><td style={tdR}>{pct(sh.pct)}</td><td style={{ ...tdR, color: BLUE, fontWeight: 600 }}>{money(sh.incurred)}</td></>}
                    {showRebate && <><td style={tdR}>{money(rb.reduction)}</td><td style={tdR}>{money(rb.net_fees)}</td></>}
                    <td style={td} title={(r.issues || []).map((i) => `${i.severity.toUpperCase()}: ${i.field}`).join("\n")}>
                      {r.issue_count > 0 ? `${r.issue_count} ⚠` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* EXCEPTIONS */}
      {tab === "exceptions" && (
        !exceptions ? <div style={{ color: SLATE, padding: 30, textAlign: "center" }}>Run a preview to compute exceptions.</div> :
        exceptions.exceptions.length === 0 ? <div style={{ color: SEM.ready.fg, padding: 30, textAlign: "center", fontWeight: 600 }}>No exceptions — all in-scope claims are ready.</div> :
        <div style={{ overflowX: "auto", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, maxHeight: "60vh" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>
              <th style={th}>Status</th><th style={th}>Claim #</th><th style={th}>Insured</th>
              <th style={thR}>Issues</th><th style={th}>Highest</th><th style={th}>Missing / Issue Summary</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {exceptions.exceptions.map((x) => (
                <tr key={x.claim_id} style={{ background: x.highest_severity === "error" ? SEM.error.bg : "#fffdf5" }}>
                  <td style={td}><StatusChip status={x.status} /></td>
                  <td style={{ ...td, color: BLUE, fontWeight: 600, cursor: onSelectClaim ? "pointer" : "default" }}
                    onClick={() => onSelectClaim && onSelectClaim(x.claim_id)}>{x.claim_number}</td>
                  <td style={td}>{x.insured_name || "—"}</td>
                  <td style={tdR}>{x.issue_count}</td>
                  <td style={td}><StatusChip status={x.highest_severity} /></td>
                  <td style={{ ...td, whiteSpace: "normal", maxWidth: 420 }}>{x.summary}</td>
                  <td style={td}>{onSelectClaim && <button onClick={() => onSelectClaim(x.claim_id)}
                    style={{ padding: "4px 10px", border: `1px solid ${BLUE}`, color: BLUE, background: "white", borderRadius: 5, cursor: "pointer", fontSize: 12 }}>Open</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RUN HISTORY */}
      {tab === "runs" && (
        !runs ? <div style={{ color: SLATE, padding: 30, textAlign: "center" }}>Loading run history…</div> :
        runs.length === 0 ? <div style={{ color: SLATE, padding: 30, textAlign: "center" }}>No runs yet.</div> :
        <div style={{ overflowX: "auto", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>
              <th style={th}>Run</th><th style={th}>Profile</th><th style={th}>Purpose</th><th style={th}>Period</th>
              <th style={thR}>Claims</th><th style={th}>By</th><th style={th}>When</th><th style={th}>Output</th>
            </tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td style={td}>#{r.id}</td>
                  <td style={td}>{r.profile_name}</td>
                  <td style={td}>{r.purpose === "client_deliverable" ? "Deliverable" : "Self-service"}</td>
                  <td style={td}>{r.reporting_period_start} → {r.reporting_period_end}</td>
                  <td style={tdR}>{r.claim_count}</td>
                  <td style={td}>{r.run_by_name || "—"}</td>
                  <td style={td}>{(r.run_at || "").replace("T", " ").slice(0, 16)}</td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 11 }}>{r.output_sha256 ? r.output_sha256.slice(0, 12) + "…" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* NOTES */}
      {tab === "notes" && (
        <div style={{ background: "white", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, padding: 20, maxWidth: 760 }}>
          <h3 style={{ marginTop: 0, color: NAVY }}>{activeProfile?.company_name || "Client"}</h3>
          <div style={{ color: SLATE, fontSize: 13, marginBottom: 10 }}>
            Profile: <b>{activeProfile?.profile_name}</b> · Kind: {activeProfile?.profile_kind}
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "#334155" }}>{activeProfile?.report_notes || "No notes recorded for this profile."}</p>
          <div style={{ marginTop: 16, fontSize: 12, color: SLATE, borderTop: "1px solid var(--border-light,#f1f5f9)", paddingTop: 12 }}>
            <b>Legend:</b>{" "}
            <span style={{ color: SEM.ready.fg }}>● Ready</span>{"  "}
            <span style={{ color: SEM.warning.fg }}>● Warning (review)</span>{"  "}
            <span style={{ color: SEM.error.fg }}>● Error (financials incomplete / blocked)</span>
          </div>
        </div>
      )}
    </div>
  );
}
